import Button from 'components/button'
import { CloseRounded } from 'components/icons'
import { EditModeProvider } from 'purchasers/editMode'
import { Dialog } from 'tamagui'

export const dontCloseOnBackgroundClick = (onClose: () => void) => (event: any, reason?: string) => {
    if (reason === 'backdropClick') {
        return
    }
    onClose()
}

interface ModalProps {
    open?: boolean
    onOpenChange?: (open: boolean) => void
    children: React.ReactNode
    width?: string | number
    height?: string | number
    maxWidth?: string | number
    maxHeight?: string | number
    /** Hide the default top-right close (X) button. */
    hideCloseButton?: boolean
}

export default function Modal({
    children,
    open,
    onOpenChange,
    width = '450px',
    height,
    maxWidth,
    maxHeight,
    hideCloseButton,
}: ModalProps) {
    return (
        <EditModeProvider defaultEdit={true}>
            <Dialog
                open={open}
                onOpenChange={onOpenChange}
            >
                <Dialog.Portal>
                    <Dialog.Overlay
                        key="overlay"
                        animation="quick"
                        opacity={0.4}
                        backgroundColor="$overlay"
                        enterStyle={{ opacity: 0 }}
                        exitStyle={{ opacity: 0 }}
                    />
                    <Dialog.Content
                        key="content"
                        // Don't dismiss on a click/focus outside the modal — only an
                        // explicit close (button / Escape) should close it. Tamagui's
                        // onOpenChange only receives (open), so blocking the backdrop
                        // has to happen here on the content's dismiss events.
                        onPointerDownOutside={e => e.preventDefault()}
                        onInteractOutside={e => e.preventDefault()}
                        bordered
                        elevate
                        animation={[
                            'quick',
                            {
                                opacity: {
                                    overshootClamping: true,
                                },
                            },
                        ]}
                        enterStyle={{ x: 0, y: -16, opacity: 0, scale: 0.96 }}
                        exitStyle={{ x: 0, y: 8, opacity: 0, scale: 0.98 }}
                        gap="$4"
                        padding="$5"
                        borderRadius="$5"
                        maxWidth={maxWidth ?? '90vw'}
                        overflow="hidden"
                        display="flex"
                        flexDirection="column"
                        {...(width && { width })}
                        {...(height && { height })}
                        {...(maxHeight && { maxHeight })}
                    >
                        {!hideCloseButton && onOpenChange && (
                            <Button
                                aria-label="Close"
                                size="$2"
                                circular
                                chromeless
                                position="absolute"
                                top="$3"
                                right="$3"
                                zIndex={1}
                                // Route through onOpenChange so per-modal close
                                // guards (e.g. "block close while submitting") apply.
                                onPress={() => onOpenChange(false)}
                            >
                                <CloseRounded size={18} />
                            </Button>
                        )}
                        {children}
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog>
        </EditModeProvider>
    )
}
